/**
 * MessagingRealtimeProvider — shared broad WebSocket subscription for messaging.
 *
 * Architecture:
 * - ONE broad subscription per browser tab (mounted at app root or messaging shell).
 * - Consumers (unread badge, sidebar) subscribe to a local event emitter instead of
 *   each opening their own WebSocket.
 *
 * The broad subscription uses `identifier=…|message` so new message creates are
 * delivered reliably (nav badge + inbox). Recipient-scoped criteria are avoided
 * due to meta.account compartment gating across Patient/Provider.
 */

import type { Communication } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react-hooks';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { isMessagingParticipantProfile } from '../services/messagingIdentity';
import { broadMessagingSubscriptionCriteria } from '../services/messagingFhir';
import { useCommunicationSubscription } from './useCommunicationSubscription';

const isDev = import.meta.env.DEV;

// ── Event emitter for local subscribers ─────────────────────────────────────

export type RealtimeMessageHandler = (communication: Communication) => void;
export type RealtimeReconnectHandler = () => void;

interface MessagingRealtimeContextValue {
  /** Subscribe to incoming Communications. Returns unsubscribe function. */
  onMessage: (handler: RealtimeMessageHandler) => () => void;
  /** Subscribe to reconnection events. Returns unsubscribe function. */
  onReconnect: (handler: RealtimeReconnectHandler) => () => void;
  /** Whether the broad WebSocket is connected. */
  connected: boolean;
}

const MessagingRealtimeContext = createContext<MessagingRealtimeContextValue | null>(null);

// ── Provider ────────────────────────────────────────────────────────────────

export interface MessagingRealtimeProviderProps {
  children: React.ReactNode;
}

export function MessagingRealtimeProvider({
  children,
}: MessagingRealtimeProviderProps): JSX.Element {
  const profile = useMedplumProfile();
  const messageListeners = useRef<Set<RealtimeMessageHandler>>(new Set());
  const reconnectListeners = useRef<Set<RealtimeReconnectHandler>>(new Set());

  const isParticipant = useMemo(
    () => !!profile?.id && isMessagingParticipantProfile(profile),
    [profile],
  );

  // Message-scoped identifier criteria (proven for nav badge + sidebar realtime).
  const subscriptionCriteria = useMemo(() => {
    if (!isParticipant) return undefined;
    return broadMessagingSubscriptionCriteria();
  }, [isParticipant]);

  useEffect(() => {
    if (!isDev || !subscriptionCriteria) return;
    console.log(
      '[Messaging][Realtime] Shared broad subscription active — criteria:',
      subscriptionCriteria,
    );
    return () => {
      console.log('[Messaging][Realtime] Shared broad subscription unmounted');
    };
  }, [subscriptionCriteria]);

  const handleCommunicationEvent = useCallback((comm: Communication) => {
    if (isDev) {
      console.log(
        '[Messaging][Realtime] Broadcasting to',
        messageListeners.current.size,
        'listener(s):',
        comm.id,
        'partOf:',
        comm.partOf?.[0]?.reference,
      );
    }
    for (const handler of messageListeners.current) {
      try {
        handler(comm);
      } catch (err) {
        console.error('[Messaging][Realtime] Listener error:', err);
      }
    }
  }, []);

  const handleReconnect = useCallback(() => {
    if (isDev) {
      console.log(
        '[Messaging][Realtime] Reconnect → notifying',
        reconnectListeners.current.size,
        'listener(s)',
      );
    }
    for (const handler of reconnectListeners.current) {
      try {
        handler();
      } catch (err) {
        console.error('[Messaging][Realtime] Reconnect listener error:', err);
      }
    }
  }, []);

  const { connected } = useCommunicationSubscription(subscriptionCriteria, {
    onCommunicationEvent: handleCommunicationEvent,
    onReconnect: handleReconnect,
  });

  const onMessage = useCallback((handler: RealtimeMessageHandler) => {
    messageListeners.current.add(handler);
    return () => {
      messageListeners.current.delete(handler);
    };
  }, []);

  const onReconnect = useCallback((handler: RealtimeReconnectHandler) => {
    reconnectListeners.current.add(handler);
    return () => {
      reconnectListeners.current.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({ onMessage, onReconnect, connected }),
    [onMessage, onReconnect, connected],
  );

  return (
    <MessagingRealtimeContext.Provider value={value}>
      {children}
    </MessagingRealtimeContext.Provider>
  );
}

// ── Consumer hook ───────────────────────────────────────────────────────────

/**
 * Subscribe to the shared broad messaging WebSocket. Must be rendered inside
 * MessagingRealtimeProvider.
 *
 * @returns Subscription helpers and connection state.
 */
export function useMessagingRealtime(): MessagingRealtimeContextValue {
  const ctx = useContext(MessagingRealtimeContext);
  if (!ctx) {
    throw new Error('useMessagingRealtime must be used inside MessagingRealtimeProvider');
  }
  return ctx;
}

export function useMessagingRealtimeOptional(): MessagingRealtimeContextValue | null {
  return useContext(MessagingRealtimeContext);
}

// ── Debounced thread refresh utility ────────────────────────────────────────

/**
 * Coalesces multiple thread-refresh requests by thread ID within a debounce window.
 * Ensures no events are lost if requests arrive while a fetch is in-flight.
 *
 * @param executeFn - Called with the set of thread IDs that need refreshing.
 * @param debounceMs - Coalescing window (default 300ms).
 */
export function createThreadRefreshCoalescer(
  executeFn: (threadIds: Set<string> | 'full') => void,
  debounceMs = 300,
): {
  /** Queue a targeted thread refresh. Pass null for full refetch. */
  enqueue: (threadId: string | null) => void;
  /** Cancel any pending debounce (for cleanup). */
  cancel: () => void;
} {
  let pending: Set<string> | 'full' = new Set();
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush(): void {
    timer = null;
    const batch = pending;
    pending = new Set();
    executeFn(batch);
  }

  function enqueue(threadId: string | null): void {
    if (threadId === null || pending === 'full') {
      pending = 'full';
    } else {
      (pending as Set<string>).add(threadId);
    }
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(flush, debounceMs);
  }

  function cancel(): void {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    pending = new Set();
  }

  return { enqueue, cancel };
}
